<template>
  <div id="form-lead-magnet" class="lead-magnet">
    <template v-if="!isSuccessfullySent">
      <FormErrorMessage v-if="isFailed" />
      <div class="lead-magnet__form-wrapper">
        <StoryblokComponent
          v-for="childBlok in blok.texts"
          :key="childBlok._uid"
          :blok="childBlok"
        />
        <form class="lead-magnet__form" novalidate @submit.prevent="onSubmit">
          <FormBaseInput
            v-model="state.name"
            :placeholder="$t('common.form.fields.first_name.placeholder')"
            :disabled="isLoading"
            type="text"
            name="name"
            required
            :v="v$.name"
          />
          <FormBaseInput
            v-model="state.email"
            :placeholder="$t('common.form.fields.email.placeholder_business')"
            :disabled="isLoading"
            type="email"
            name="email"
            required
            :v="v$.email"
          />
          <div class="lead-magnet__consent">
            <FormBaseCheckbox
              v-model="state.consentToMarketing"
              class="checkbox-group__consent"
              label-i18-path="common.form.consents.unified_consent"
              name="consent-to-process"
              required
              :v="v$.consentToMarketing"
            >
              <nuxt-link target="_blank" to="/privacy-policy">
                {{ $t('common.form.privacy_policy') }}
              </nuxt-link>
            </FormBaseCheckbox>
          </div>
          <BaseButton
            :title="$t('common.form.download')"
            class="lead-magnet__submit"
            type="submit"
            :disabled="isLoading"
          >
            <template #text>
              <span v-if="!isLoading">{{
                $t('common.form.download-report')
              }}</span>
              <LoaderSpinner v-else cobalt class="lead-magnet__loader" />
            </template>
          </BaseButton>
        </form>
      </div>
    </template>
    <template v-else>
      <div class="lead-magnet__success">
        <StoryblokComponent
          v-for="childBlok in blok.thank_you_blocks"
          :key="childBlok._uid"
          :blok="childBlok"
        />
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { useVuelidate } from '@vuelidate/core'
import useValidation from '@/composables/useValidation'
import useSendData from '@/composables/useSendData'
import useGtm from '@/composables/useGtm'
import { getCookie } from '@/utils/helpers'
import type { SbFormLeadMagnetStoryblok } from '@/types'
import gsap from 'gsap'

interface Props {
  blok: SbFormLeadMagnetStoryblok
}

const props = defineProps<Props>()
const route = useRoute()
const router = useRouter()

const state = reactive({
  name: '',
  email: '',
  consentToMarketing: false,
})

const { isFieldRequired, isEmailValid, isEmailBusiness, isConsentAccepted } =
  useValidation()

const rules = {
  name: { required: isFieldRequired },
  email: {
    required: isFieldRequired,
    email: isEmailValid,
    business: isEmailBusiness,
  },
  consentToMarketing: {
    required: isConsentAccepted,
  },
}
const config = useRuntimeConfig()

const {
  scrollToError,
  isFailed,
  isSuccessfullySent,
  isLoading,
  sendFormData,
  sendGtmForm,
  getGenericFormUrl,
} = useSendData()

const v$ = useVuelidate(rules, state)
const {
  gtmNewsletterSign,
  gtmCompanyTypeData,
  gtmLeadProcessData,
  userId,
  companyId,
} = useGtm(state, v$.value.$errors)

const goToThankYouView = () => {
  const tl = gsap.timeline()

  tl.to('.lead-magnet__form-wrapper', {
    opacity: 0,
    y: 20,
    duration: 0.3,
  }).then(() => {
    isSuccessfullySent.value = true
    tl.fromTo(
      '.lead-magnet__success',
      { opacity: 0, y: 20 },
      { opacity: 1, y: 0, duration: 0.3 },
    )
  })
}

const onSubmit = async () => {
  v$.value.$touch()

  const data = {
    submittedAt: Date.now(),
    fields: [
      {
        name: 'email',
        value: state.email,
      },
      {
        name: 'firstname',
        value: state.name,
      },
      {
        name: 'user_id__infermedica_',
        value: userId(state.email),
      },
      {
        name: 'company_id__infermedica_',
        value: companyId(state.email),
      },
      {
        name: 'user_s_origin',
        value: router.options.history.state.back || '',
      },
    ],
    context: {
      hutk: getCookie('hubspotutk'),
      pageUri: window.location.href,
      pageName: route.name,
    },
    legalConsentOptions: {
      consent: {
        consentToProcess: true,
        text: 'I want to receive prepared material and other similar content.',
        communications: [
          {
            value: state.consentToMarketing,
            subscriptionTypeId: 6450043,
            text: 'I agree to receive occasional marketing emails from Infermedica about their products and services. More information can be found in Privacy Policy, and I can withdraw my consent at any time.',
          },
        ],
      },
    },
  }

  if (!v$.value.$invalid) {
    gtmNewsletterSign()

    await sendFormData(getGenericFormUrl(props.blok.hubspot_form_url), data, {
      thankYouRoute: '',
      offset: 200,
    })

    await sendGtmForm(
      config.public.analytics.leadProcessUrl,
      gtmCompanyTypeData,
    )
    await sendGtmForm(
      config.public.analytics.leadProcessUrl,
      gtmLeadProcessData,
    )

    goToThankYouView()
    return
  }

  await nextTick()
  scrollToError('#form-lead-magnet')
}
</script>

<style lang="scss" scoped>
.lead-magnet {
  &__form {
    display: flex;
    flex-direction: column;
    max-width: 403px;
  }

  &__consent {
    margin-bottom: var(--space48);
  }

  &__submit {
    display: block !important;
  }

  &__loader {
    transform: scale(0.4) translate(-24px, -26px);
    width: 48px;
    height: 24px;
  }

  &__success {
    min-height: 600px;
    max-width: 480px;
    display: flex;
    flex-direction: column;
    justify-content: center;
  }
}
</style>
